import type { Voice } from '@listening/api-contract/legacy-client-types';
import {
  PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES,
  PUBLIC_TOOL_PDF_MAX_PAGES,
  PUBLIC_TOOL_UPLOAD_DOCUMENT_TYPES,
  PublicToolUploadDocumentType,
  isPublicToolUploadDocumentType,
} from '@listening/api-contract';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  documentTypeAccept,
  resolveDocumentTypeForFile,
  VoiceModelId,
} from '@listening/api-contract/schemas';
import {
  atomKvsSync,
  kvsSyncReader,
  sessionKvsStorage,
} from '@listening/frontend/atoms/kvs';
import { useAtomSet } from '@effect/atom-react';
import { clientEnv } from '@/env';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@listening/frontend/components/radix/popover';
import { useMutation, useQuery, queryOptions } from '@tanstack/react-query';
import { useBlocker, useNavigate, useSearch } from '@tanstack/react-router';
import { Clock, Data, Effect, Predicate, Schema } from 'effect';
import { HttpClient, HttpClientRequest } from 'effect/http';
import { Code, Page, Refresh, RefreshDouble, Upload } from 'iconoir-react';
import {
  NavArrowDown as NavArrowDownSolid,
  WarningCircle as WarningCircleSolid,
} from 'iconoir-react/solid';
import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import {
  navigateToPublicToolReadAlong,
  waitForPublicToolReadAlong,
} from '@/lib/public-tool-job';
import { prewarmTurnstileToken } from '@/lib/turnstile';
import {
  catchPublicToolHttpError,
  takeTurnstileTokenEffect,
} from '@/lib/public-tool-errors';
import { capturePublicToolClientTiming } from '@/lib/public-tool-client-timing';
import { formatBytes } from '@/lib/format-bytes';
import { voiceLangToLangLabel } from '@/lib/utils';
import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { eq } from '@/runtimes/effect-query';
import { callApiPromise } from '@/runtimes/get-runtime';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'pdf-to-audio-converter' });

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5] as const;
const PDF_JOB_MAX_POLLS = 240;
const PUBLIC_DOCUMENT_ACCEPT = [
  ...new Set(
    Object.entries(
      documentTypeAccept(PUBLIC_TOOL_UPLOAD_DOCUMENT_TYPES),
    ).flatMap(([mediaType, extensions]) => [mediaType, ...extensions]),
  ),
].join(',');
type Speed = (typeof SPEED_OPTIONS)[number];

// Client-side failures of the document pipeline, carried in the mutation's
// typed error channel next to the contract's own errors (BadRequestError,
// CaptchaError, RateLimitedError, InternalServerError). Distinct tags so the
// alert picks copy with `error.match` rather than rendering a raw message.
class PdfNoFileSelectedError extends Data.TaggedError(
  'PdfNoFileSelectedError',
) {}
class PdfUploadFailedError extends Data.TaggedError('PdfUploadFailedError') {}
class PdfUnexpectedToolError extends Data.TaggedError(
  'PdfUnexpectedToolError',
) {}
class PdfJobFailedError extends Data.TaggedError('PdfJobFailedError') {}

const PDF_GENERATION_FAILED_COPY =
  'Could not generate audio from this document. Please try again later.';

// Anything the matcher does not name: an unknown failure tag or a defect.
const PDF_UNEXPECTED_FAILURE_COPY =
  'Could not generate audio from this document. Please try another file.';

function describeVoice(voice: Voice) {
  const gender =
    voice.gender === 'male'
      ? m.settings_voice_male()
      : m.settings_voice_female();
  const softness =
    voice.softness === 'soft'
      ? m.settings_voice_soft()
      : voice.softness === 'deep'
        ? m.settings_voice_deep()
        : m.settings_voice_moderate();
  const language =
    voice.lang === 'en' ? null : voiceLangToLangLabel(voice.lang);
  return [gender, softness, language].filter(Boolean).join(' · ');
}

function pickInitialVoice({
  preferredGender,
  preferredLang,
  voices,
}: {
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  voices: ReadonlyArray<Voice>;
}): Voice | undefined {
  const locale = getLocale();
  return (
    voices.find(
      (v) =>
        v.lang === (preferredLang ?? locale) &&
        (!preferredGender || v.gender === preferredGender),
    ) ??
    voices.find((v) => v.lang === (preferredLang ?? locale)) ??
    voices.find((v) => !preferredGender || v.gender === preferredGender) ??
    voices[0]
  );
}

type PdfProcessStage = 'uploading' | 'submitting' | 'pending' | 'processing';
const StoredPdfJob = Schema.Struct({
  fileName: Schema.String,
  documentType: PublicToolUploadDocumentType,
  fileSizeBytes: Schema.Finite,
  startedAtMs: Schema.Finite,
  voice: Schema.optional(VoiceModelId),
  documentUrl: Schema.optional(Schema.String),
  jobId: Schema.optional(Schema.String),
  stage: Schema.optional(
    Schema.Literals(['uploading', 'submitting', 'pending', 'processing']),
  ),
});

type PdfJob = typeof StoredPdfJob.Type & { file?: File | undefined };

/**
 * The tab's in-flight upload or job: resumes an upload that has no job yet,
 * and keeps the file's name and size for a job resumed in the same tab.
 */
const resumablePdfJobKvs = {
  key: 'public-pdf-to-audio:resumable-job',
  schema: Schema.NullOr(StoredPdfJob),
  defaultValue: (): PdfJob | null => null,
  storage: sessionKvsStorage,
};
const resumablePdfJobAtom = atomKvsSync(resumablePdfJobKvs);
const readResumablePdfJob = kvsSyncReader(resumablePdfJobKvs);

type PdfGeneratePayload = {
  file?: File | undefined;
  documentUrl?: string | undefined;
  jobId?: string | undefined;
  voice?: Voice['model_id'] | undefined;
  fileSizeBytes: number;
  fileName: string;
  documentType: typeof PublicToolUploadDocumentType.Type;
  startedAtMs?: number | undefined;
};

/**
 * Completes when the attempt is abandoned (reset, a newer attempt, unmount).
 * Racing an attempt against it interrupts the upload, submit and poll.
 */
const abandonedBy = (signal: AbortSignal) =>
  Effect.callback<void>((resume) => {
    const onAbort = () => resume(Effect.void);
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    return Effect.sync(() => signal.removeEventListener('abort', onAbort));
  });

/**
 * The job poll runs over `GetJob`, whose own failures (job failed, poll
 * timeout, transport error) arrive as untyped rejections. Collapse them into
 * one tag — the rejection message can carry the request URL, so it is logged
 * rather than shown.
 */
const waitForJob = <A,>(
  label: string,
  wait: (signal: AbortSignal) => Promise<A>,
) =>
  Effect.tryPromise({
    try: wait,
    catch: (cause) => {
      moduleLogger.error({ err: cause }, `[Listening] ${label} failed`);
      return new PdfJobFailedError();
    },
  });

function requireSelectedPdfFile(file: File | undefined) {
  return file
    ? Effect.succeed(file)
    : Effect.fail(new PdfNoFileSelectedError());
}

function makePdfUploadFormData(
  fields: Record<string, string>,
  file: File,
): FormData {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);
  return formData;
}

function estimatePdfConversionSeconds(bytes: number) {
  const mb = bytes / 1024 / 1024;
  const min = Math.round(Math.min(240, Math.max(75, 70 + mb * 12)));
  const max = Math.round(Math.min(420, Math.max(min + 45, 120 + mb * 18)));
  return { min, max };
}

function formatMinutes(seconds: number) {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes.toString()} min`;
}

function formatEstimate(bytes: number) {
  const estimate = estimatePdfConversionSeconds(bytes);
  return `${formatMinutes(estimate.min)}-${formatMinutes(estimate.max)}`;
}

const PDF_STAGE_LABELS = {
  uploading: 'Uploading document',
  submitting: 'Starting conversion',
  pending: 'Queued for conversion',
  processing: 'Converting document to audio',
} satisfies Record<PdfProcessStage, string>;

const PDF_STAGE_PROGRESS = {
  uploading: 18,
  submitting: 35,
  pending: 50,
  processing: 62,
} satisfies Record<PdfProcessStage, number>;

const PDF_STAGE_ORDER = {
  uploading: 0,
  submitting: 1,
  pending: 2,
  processing: 3,
} satisfies Record<PdfProcessStage, number>;

function getStageLabel(stage: PdfProcessStage) {
  return PDF_STAGE_LABELS[stage];
}

function getLaterStage(
  current: PdfProcessStage | undefined,
  next: PdfProcessStage,
) {
  return current && PDF_STAGE_ORDER[current] > PDF_STAGE_ORDER[next]
    ? current
    : next;
}

function isPdfProcessStage(status: string): status is PdfProcessStage {
  return status in PDF_STAGE_ORDER;
}

function getProgressPercent({
  fileSizeBytes,
  startedAtMs,
  nowMs,
  stage,
}: {
  fileSizeBytes: number;
  startedAtMs: number;
  nowMs: number;
  stage: PdfProcessStage;
}) {
  if (stage !== 'processing') return PDF_STAGE_PROGRESS[stage];

  const estimate = estimatePdfConversionSeconds(fileSizeBytes);
  const elapsedSeconds = Math.max(0, (nowMs - startedAtMs) / 1000);
  const baseProgress = PDF_STAGE_PROGRESS.processing;
  const progressRange = 95 - baseProgress;
  return Math.min(
    95,
    Math.max(
      baseProgress,
      Math.round(
        baseProgress + (elapsedSeconds / estimate.max) * progressRange,
      ),
    ),
  );
}

function setPdfJobStage(
  setPdfJob: Dispatch<SetStateAction<PdfJob | null>>,
  stage: PdfProcessStage,
) {
  setPdfJob((job) =>
    job ? { ...job, stage: getLaterStage(job.stage, stage) } : job,
  );
}

function syncStageFromJobStatus(
  status: { status: string },
  setPdfJob: Dispatch<SetStateAction<PdfJob | null>>,
) {
  if (!isPdfProcessStage(status.status)) return;
  const stage = status.status;
  setPdfJob((job) =>
    job?.jobId ? { ...job, stage: getLaterStage(job.stage, stage) } : job,
  );
}

export function PdfToAudioConverter({
  heading = 'PDF to audio',
  preferredGender,
  preferredLang,
  subtitle = 'Drop a PDF, pick a natural AI voice, and listen to it as audio.',
}: {
  heading?: string | undefined;
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  subtitle?: string | undefined;
}) {
  const voicesQuery = useQuery(
    queryOptions({
      queryKey: ['tts-tool', 'voices'],
      queryFn: () =>
        callApiPromise((api) => api.Voices.ListVoices({ query: {} })),
      select: (response) => response.voices.filter((v) => v.is_free),
      staleTime: 1000 * 60 * 60 * 24,
    }),
  );
  const voices = voicesQuery.data ?? [];

  const [voiceOverride, setVoiceOverride] = useState<Voice['model_id']>();
  const voiceModelId =
    voiceOverride ??
    pickInitialVoice({ preferredGender, preferredLang, voices })?.model_id;
  const selectedVoice = voices.find((v) => v.model_id === voiceModelId);

  const [pdfJob, setPdfJob] = useState<PdfJob | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isResultRedirectRef = useRef(false);
  const attemptRef = useRef<AbortController | null>(null);
  const activeVoice = pdfJob?.voice ?? voiceModelId;
  const setResumableJob = useAtomSet(resumablePdfJobAtom);

  const navigate = useNavigate();
  const urlJobId = useSearch({ strict: false }).pdf_job;
  const setUrlJobId = useCallback(
    (jobId: string | undefined) =>
      navigate({
        to: '.',
        search: (search) => ({ ...search, pdf_job: jobId }),
        replace: true,
        ignoreBlocker: true,
      }),
    [navigate],
  );
  const redirectToReadAlong = useCallback(
    (
      jobId: string,
      prefs: { speed: Speed; voice: Voice['model_id'] | undefined },
      signal: AbortSignal,
    ) => {
      setResumableJob(null);
      void setUrlJobId(undefined).then(async () => {
        if (signal.aborted) return;
        isResultRedirectRef.current = true;
        await navigateToPublicToolReadAlong(navigate, jobId, {
          speed: prefs.speed,
          voice: prefs.voice,
        });
        // Going back before the reader finishes loading returns to this same
        // mounted tool, which should be empty like a fresh visit.
        isResultRedirectRef.current = false;
        setPdfJob(null);
      });
    },
    [navigate, setResumableJob, setUrlJobId],
  );

  const generate = useMutation({
    ...eq.mutationOptions({
      mutationFn: ({
        signal,
        ...payload
      }: PdfGeneratePayload & { signal: AbortSignal }) => {
        // Interruption is asynchronous, so an abandoned attempt can still
        // reach a write before its fiber stops; every write checks first.
        const whileCurrent = (write: () => void) =>
          Effect.sync(() => {
            if (!signal.aborted) write();
          });
        return Effect.gen(function* () {
          const api = yield* ApiClient;
          const startedAtMs =
            payload.startedAtMs ?? (yield* Clock.currentTimeMillis);
          const baseJob: PdfJob = {
            fileName: payload.fileName,
            documentType: payload.documentType,
            fileSizeBytes: payload.fileSizeBytes,
            startedAtMs,
            voice: payload.voice,
          };
          // A resumed attempt starts with nothing on screen.
          yield* whileCurrent(() =>
            setPdfJob(
              (job) =>
                job ?? {
                  ...baseJob,
                  documentUrl: payload.documentUrl,
                  jobId: payload.jobId,
                  stage: payload.jobId ? 'pending' : 'submitting',
                },
            ),
          );
          const submitNewJob = Effect.gen(function* () {
            let documentUrl = payload.documentUrl;

            if (!documentUrl) {
              const file = yield* requireSelectedPdfFile(payload.file);
              yield* whileCurrent(() =>
                setPdfJob({ ...baseJob, file, stage: 'uploading' }),
              );

              const uploadToken = yield* takeTurnstileTokenEffect(
                clientEnv.turnstileSiteKey,
              );
              // The submit step needs its own token (single-use) — warm it while
              // the upload is in flight.
              yield* Effect.sync(() =>
                prewarmTurnstileToken({ sitekey: clientEnv.turnstileSiteKey }),
              );
              const upload = yield* api.PublicTools.PdfUploadUrl({
                payload: {
                  filename: payload.fileName,
                  fileSizeBytes: payload.fileSizeBytes,
                  cf_turnstile_token: uploadToken,
                },
              }).pipe(
                Effect.catchTag(
                  'HttpClientError',
                  catchPublicToolHttpError('pdf-to-audio'),
                ),
              );

              documentUrl = upload.document_url;
              const uploadedJob: PdfJob = {
                ...baseJob,
                file,
                documentUrl,
                stage: 'uploading',
              };
              yield* whileCurrent(() => setPdfJob(uploadedJob));

              const revertToSelected = whileCurrent(() =>
                setPdfJob({ ...baseJob, file }),
              );

              const http = yield* HttpClient.HttpClient;
              const uploadResponse = yield* HttpClientRequest.post(
                upload.signed_upload_url,
              ).pipe(
                HttpClientRequest.bodyFormData(
                  makePdfUploadFormData(upload.fields, file),
                ),
                http.execute,
                Effect.scoped,
                Effect.mapError(() => new PdfUploadFailedError()),
                Effect.catch((error) =>
                  revertToSelected.pipe(Effect.andThen(error)),
                ),
              );
              if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
                yield* revertToSelected;
                return yield* new PdfUploadFailedError();
              }
              yield* whileCurrent(() => setResumableJob(uploadedJob));
            }

            yield* whileCurrent(() => setPdfJobStage(setPdfJob, 'submitting'));
            const submitStart = performance.now();
            const submitToken = yield* takeTurnstileTokenEffect(
              clientEnv.turnstileSiteKey,
            );
            const turnstileDoneAt = performance.now();
            const job = yield* api.PublicTools.PdfToAudio({
              payload: {
                document_url: documentUrl,
                fileName: payload.fileName,
                fileSizeBytes: payload.fileSizeBytes,
                voice: payload.voice,
                cf_turnstile_token: submitToken,
              },
            }).pipe(
              Effect.catchTag(
                'HttpClientError',
                catchPublicToolHttpError('pdf-to-audio'),
              ),
            );
            const timing = {
              turnstileMs: turnstileDoneAt - submitStart,
              createJobMs: performance.now() - turnstileDoneAt,
            };
            const runningJob: PdfJob = {
              ...baseJob,
              documentUrl,
              jobId: job.job_id,
              stage: 'pending',
            };
            yield* whileCurrent(() => {
              setResumableJob(runningJob);
              setPdfJob(runningJob);
              void setUrlJobId(job.job_id);
            });
            return { jobId: job.job_id, timing };
          });

          const { jobId, timing } = payload.jobId
            ? { jobId: payload.jobId, timing: undefined }
            : yield* submitNewJob;
          // Resolves as soon as the read-along is openable (conversion done,
          // TTS still synthesizing) instead of waiting for the full pipeline.
          const waitStart = performance.now();
          const ready = yield* waitForJob(
            'pdf-to-audio read-along',
            (pollSignal) =>
              waitForPublicToolReadAlong(jobId, {
                maxPolls: PDF_JOB_MAX_POLLS,
                onStatus: (status) => {
                  if (!signal.aborted)
                    syncStageFromJobStatus(status, setPdfJob);
                },
                signal: pollSignal,
              }),
          );
          if (timing) {
            yield* Effect.sync(() =>
              capturePublicToolClientTiming({
                tool: 'pdf-to-audio',
                ...timing,
                waitReadyMs: performance.now() - waitStart,
                readyFromPartial: !ready.completed,
              }),
            );
          }
          if (ready.tool !== 'pdf-to-audio') {
            return yield* new PdfUnexpectedToolError();
          }
          const audioId = ready.audio_id;
          if (!audioId) return yield* new PdfJobFailedError();
          yield* whileCurrent(() =>
            redirectToReadAlong(
              audioId,
              { speed, voice: payload.voice },
              signal,
            ),
          );
        }).pipe(
          Effect.ensuring(
            whileCurrent(() =>
              setPdfJob((job) => (job ? { ...job, stage: undefined } : job)),
            ),
          ),
          Effect.raceFirst(abandonedBy(signal)),
        );
      },
    }),
    onError: (error, { signal }) => {
      if (signal.aborted) return;
      setResumableJob(null);
      void setUrlJobId(undefined);
      if (Predicate.isTagged(error, 'EffectQueryDefect')) {
        moduleLogger.error(
          { defectCause: error.defectCause },
          '[Listening] pdf-to-audio defect',
        );
      }
    },
  });

  const startAttempt = (payload: PdfGeneratePayload) => {
    attemptRef.current?.abort();
    const attempt = new AbortController();
    attemptRef.current = attempt;
    generate.mutate({ ...payload, signal: attempt.signal });
  };

  const visibleStage =
    pdfJob?.stage ?? (generate.isPending ? 'submitting' : null);
  const isWorking = generate.isPending || visibleStage !== null;
  const canGeneratePdf =
    Boolean(pdfJob) && Boolean(activeVoice) && !generate.isPending;
  const progressPercent =
    pdfJob && visibleStage
      ? getProgressPercent({
          fileSizeBytes: pdfJob.fileSizeBytes,
          startedAtMs: pdfJob.startedAtMs,
          nowMs,
          stage: visibleStage,
        })
      : 0;
  // A defect (network/parse) carries no tag, so it lands in `OrElse`; `onError`
  // logs it. Never render its `.message` — that's `Cause.pretty(cause)`.
  const generateErrorMessage = generate.error
    ? generate.error.match({
        PublicToolCaptchaError: () => m.try_magic_error_captcha(),
        CaptchaError: () => m.try_magic_error_captcha(),
        // Server-authored validation copy (upload rejected, tools disabled, ...).
        BadRequestError: (error) => error.message,
        RateLimitedError: (error) =>
          error.scope === 'global'
            ? error.message
            : m.try_magic_error_rate_limit(),
        PublicToolRateLimitedError: () => m.try_magic_error_rate_limit(),
        PublicToolUnavailableError: () =>
          'Document to audio is not available in this environment yet.',
        PublicToolRequestFailedError: () => PDF_GENERATION_FAILED_COPY,
        InternalServerError: () => PDF_GENERATION_FAILED_COPY,
        PdfNoFileSelectedError: () => 'Please choose a PDF or Markdown file.',
        PdfUploadFailedError: () =>
          'Could not upload this document. Please try again.',
        PdfUnexpectedToolError: () => 'Unexpected public tool response',
        PdfJobFailedError: () => PDF_GENERATION_FAILED_COPY,
        OrElse: () => PDF_UNEXPECTED_FAILURE_COPY,
      })
    : null;
  const errorMessage = generateErrorMessage ?? fileError;

  useEffect(() => {
    if (!isWorking) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isWorking]);

  // Resuming is a mount-time decision only: once this tab has started or
  // abandoned an attempt, a `?pdf_job` that is still being cleared must not
  // restart it. Storage is read directly because the atom renders its default
  // until hydration finishes.
  const resumeOnMount = useEffectEvent(() => {
    const stored = readResumablePdfJob();
    // Another tab or device has only the job id from the URL, not the file.
    const sameTab =
      urlJobId === undefined || stored?.jobId === urlJobId ? stored : null;
    const jobId = urlJobId ?? sameTab?.jobId;
    if (!jobId && !sameTab?.documentUrl) return;
    const resumed: PdfJob = sameTab ?? {
      fileName: 'Uploaded document',
      documentType: 'pdf',
      fileSizeBytes: 0,
      startedAtMs: Date.now(),
    };
    startAttempt({
      jobId,
      documentUrl: resumed.documentUrl,
      documentType: resumed.documentType,
      voice: resumed.voice,
      fileName: resumed.fileName,
      fileSizeBytes: resumed.fileSizeBytes,
      startedAtMs: resumed.startedAtMs,
    });
  });

  useEffect(() => {
    resumeOnMount();
    return () => attemptRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!isWorking) return;
    const handler = (event: BeforeUnloadEvent) => {
      if (isResultRedirectRef.current) return;
      event.preventDefault();
      // oxlint-disable-next-line typescript/no-deprecated -- Chromium < 119 only prompts when returnValue is set
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isWorking]);

  const blocker = useBlocker({
    shouldBlockFn: () => isWorking && !isResultRedirectRef.current,
    withResolver: true,
  });

  const resetTool = () => {
    attemptRef.current?.abort();
    attemptRef.current = null;
    setPdfJob(null);
    setFileError(null);
    generate.reset();
    setResumableJob(null);
    void setUrlJobId(undefined);
  };

  const handleFile = (file: File) => {
    resetTool();
    // Warm the CAPTCHA token while the user reviews their selection — the
    // upload step consumes it, keeping the round-trip off the critical path.
    prewarmTurnstileToken({ sitekey: clientEnv.turnstileSiteKey });
    const documentType = resolveDocumentTypeForFile(file.name, file.type);
    if (!isPublicToolUploadDocumentType(documentType)) {
      setFileError('Please choose a PDF or Markdown file.');
      return;
    }
    if (file.size > PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES) {
      setFileError(
        `Document is larger than the ${Math.floor(
          PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES / 1024 / 1024,
        ).toString()} MB limit.`,
      );
      return;
    }
    setPdfJob({
      file,
      fileName: file.name,
      documentType,
      fileSizeBytes: file.size,
      startedAtMs: Date.now(),
    });
  };

  const onPickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onSubmit = () => {
    if (!pdfJob || generate.isPending) return;
    if (!activeVoice) return;
    startAttempt({
      file: pdfJob.file,
      documentUrl: pdfJob.documentUrl,
      documentType: pdfJob.documentType,
      voice: activeVoice,
      fileName: pdfJob.fileName,
      fileSizeBytes: pdfJob.fileSizeBytes,
      startedAtMs: pdfJob.startedAtMs,
    });
  };

  return (
    <section
      aria-labelledby='pdf-to-audio-tool-heading'
      className='mx-auto max-w-6xl px-4 pt-10 pb-16 sm:px-6 sm:pt-14 lg:px-8'
    >
      <Dialog
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
        open={blocker.status === 'blocked'}
      >
        <DialogContent className='max-w-sm rounded-2xl border border-navy-220/10 bg-white p-6'>
          <DialogHeader>
            <DialogTitle className='text-base font-semibold text-navy-220'>
              Leave while your document is converting?
            </DialogTitle>
            <DialogDescription className='mt-2 text-sm leading-6 text-navy-220/65'>
              Your conversion is still running. Once the job starts, this URL
              can recover it, but staying here is the safest way to keep
              progress visible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='mt-4 flex gap-2 sm:gap-2'>
            <button
              className='flex-1 rounded-full border border-navy-220/15 px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5'
              onClick={() => blocker.reset?.()}
              type='button'
            >
              Stay
            </button>
            <button
              className='flex-1 rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-600'
              onClick={() => blocker.proceed?.()}
              type='button'
            >
              Leave
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className='mb-8 text-center sm:mb-10'>
        <h1
          className='text-3xl font-semibold tracking-[-0.04em] text-navy-220 sm:text-5xl'
          id='pdf-to-audio-tool-heading'
        >
          {heading}
        </h1>
        <p className='mx-auto mt-3 max-w-2xl text-base leading-7 text-navy-220/65 sm:text-lg'>
          {subtitle}
        </p>
      </div>

      <div className='rounded-[28px] border border-navy-220/10 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]'>
        {!pdfJob && (
          <div className='p-6 sm:p-10'>
            <button
              className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition ${
                isDragging
                  ? 'border-royal-50 bg-royal-50/5'
                  : 'border-navy-220/15 bg-navy-220/[0.02] hover:border-navy-220/30'
              }`}
              disabled={generate.isPending}
              onClick={() => fileInputRef.current?.click()}
              onDragLeave={() => setIsDragging(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDrop={onDrop}
              type='button'
            >
              {generate.isPending ? (
                <RefreshDouble
                  aria-hidden='true'
                  className='size-10 animate-spin text-royal-50'
                />
              ) : (
                <Upload
                  aria-hidden='true'
                  className='size-10 text-navy-220/50'
                />
              )}
              <span className='text-lg font-semibold text-navy-220'>
                {generate.isPending
                  ? 'Preparing your document…'
                  : 'Drop a PDF or Markdown file'}
              </span>
              <span className='text-sm text-navy-220/60'>
                PDF and Markdown files up to{' '}
                {Math.floor(
                  PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES / 1024 / 1024,
                ).toString()}{' '}
                MB · reads the first {PUBLIC_TOOL_PDF_MAX_PAGES.toString()}{' '}
                pages of a PDF
              </span>
            </button>
            <input
              accept={PUBLIC_DOCUMENT_ACCEPT}
              className='hidden'
              onChange={onPickFile}
              ref={fileInputRef}
              type='file'
            />
          </div>
        )}

        {pdfJob && (
          <div className='p-6 sm:p-10'>
            <div className='flex items-start gap-4 rounded-2xl border border-navy-220/10 bg-navy-220/[0.02] p-4 sm:p-6'>
              <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-royal-50/10 text-royal-50'>
                {pdfJob.documentType === 'md' ? (
                  <Code aria-hidden='true' className='size-6' />
                ) : (
                  <Page aria-hidden='true' className='size-6' />
                )}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-base font-semibold text-navy-220'>
                  {pdfJob.fileName}
                </p>
                <p className='mt-1 text-sm text-navy-220/60'>
                  {formatBytes(pdfJob.fileSizeBytes)} ·{' '}
                  {pdfJob.documentType === 'md' ? 'Markdown' : 'PDF'} conversion
                </p>
              </div>
              <button
                aria-label='Choose a different file'
                className='inline-flex size-9 items-center justify-center rounded-full text-navy-220/60 transition hover:bg-navy-220/5 hover:text-navy-220'
                onClick={resetTool}
                type='button'
              >
                <Refresh aria-hidden='true' className='size-4' />
              </button>
            </div>
            {isWorking && (
              <div className='mt-5 rounded-2xl border border-royal-50/15 bg-royal-50/[0.04] p-4'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div>
                    <p className='text-sm font-semibold text-navy-220'>
                      {visibleStage
                        ? getStageLabel(visibleStage)
                        : 'Preparing your audio'}
                    </p>
                    <p className='mt-1 text-sm text-navy-220/60'>
                      Estimated time: {formatEstimate(pdfJob.fileSizeBytes)}
                    </p>
                  </div>
                  <span className='text-sm font-semibold text-royal-50'>
                    {progressPercent.toString()}%
                  </span>
                </div>
                <div className='mt-3 h-2 overflow-hidden rounded-full bg-navy-220/10'>
                  <div
                    className='h-full rounded-full bg-royal-50 transition-[width] duration-500'
                    style={{ width: `${progressPercent.toString()}%` }}
                  />
                </div>
                <p className='mt-3 text-sm leading-6 text-navy-220/60'>
                  Larger files and scanned PDFs can take a few minutes. Keep
                  this page open; after the upload finishes, the URL can recover
                  this conversion if the page refreshes.
                </p>
              </div>
            )}
          </div>
        )}

        {errorMessage && (
          <div
            className='flex items-start gap-2 border-t border-red-500/15 bg-red-50/60 px-6 py-4 text-sm font-medium text-red-700 sm:px-10'
            role='alert'
          >
            <WarningCircleSolid
              aria-hidden='true'
              className='mt-0.5 size-4 shrink-0'
            />
            <span>{errorMessage}</span>
          </div>
        )}

        {pdfJob && (
          <div className='flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-navy-220/10 px-6 py-5 sm:px-10'>
            <VoicePicker
              disabled={voicesQuery.isLoading || generate.isPending}
              onSelect={(modelId) => {
                setVoiceOverride(modelId);
              }}
              selectedVoice={selectedVoice}
              voices={voices}
            />
            <SpeedSelector
              disabled={generate.isPending}
              onChange={setSpeed}
              speed={speed}
            />
          </div>
        )}

        {pdfJob && (
          <div className='border-t border-navy-220/10 p-6 sm:p-10'>
            <button
              className='flex w-full items-center justify-center gap-3 rounded-2xl bg-royal-50 px-6 py-5 text-lg font-semibold text-white transition hover:bg-navy-220 disabled:cursor-not-allowed disabled:opacity-60'
              disabled={!canGeneratePdf}
              onClick={onSubmit}
              type='button'
            >
              {generate.isPending && (
                <RefreshDouble
                  aria-hidden='true'
                  className='size-6 animate-spin'
                />
              )}
              <span>
                {generate.isPending
                  ? 'Uploading and generating audio…'
                  : 'Generate audio'}
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function VoicePicker({
  disabled,
  onSelect,
  selectedVoice,
  voices,
}: {
  disabled: boolean;
  onSelect: (modelId: Voice['model_id']) => void;
  selectedVoice: Voice | undefined;
  voices: ReadonlyArray<Voice>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Voice'
        className='group inline-flex cursor-pointer items-center gap-2 rounded-full text-sm transition focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled || voices.length === 0}
        type='button'
      >
        {selectedVoice ? (
          <>
            <img
              alt=''
              className='size-9 rounded-full object-cover ring-2 ring-white'
              height={36}
              src={selectedVoice.image_url}
              width={36}
            />
            <span className='font-medium text-navy-220'>
              {selectedVoice.name}{' '}
              <span className='text-navy-220/60'>
                · {describeVoice(selectedVoice)}
              </span>
            </span>
            <NavArrowDownSolid
              aria-hidden='true'
              className={`size-4 text-navy-220/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        ) : (
          <span className='text-navy-220/60'>{m.loading()}</span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='max-h-80 w-72 overflow-y-auto rounded-2xl border border-navy-220/10 bg-white p-2 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col gap-1' role='listbox'>
          {voices.map((voice) => {
            const isSelected = voice.model_id === selectedVoice?.model_id;
            return (
              <li key={voice.model_id}>
                <button
                  aria-selected={isSelected}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50 ${
                    isSelected ? 'bg-royal-50/5' : ''
                  }`}
                  onClick={() => {
                    onSelect(voice.model_id);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  <img
                    alt=''
                    className='size-9 rounded-full object-cover'
                    height={36}
                    src={voice.image_url}
                    width={36}
                  />
                  <span className='flex flex-col'>
                    <span className='font-medium text-navy-220'>
                      {voice.name}
                    </span>
                    <span className='text-sm text-navy-220/60'>
                      {describeVoice(voice)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function SpeedSelector({
  disabled,
  onChange,
  speed,
}: {
  disabled: boolean;
  onChange: (speed: Speed) => void;
  speed: Speed;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Speed'
        className='inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-220 transition hover:text-royal-50 disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled}
        type='button'
      >
        <span className='text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'>
          {m.tts_tool_speed_label()}
        </span>
        <span>{speed}×</span>
        <NavArrowDownSolid
          aria-hidden='true'
          className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-24 rounded-2xl border border-navy-220/10 bg-white p-1 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col' role='listbox'>
          {SPEED_OPTIONS.map((option) => {
            const isSelected = option === speed;
            return (
              <li key={option}>
                <button
                  aria-selected={isSelected}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                    isSelected ? 'font-semibold text-royal-50' : 'text-navy-220'
                  }`}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  {option}×
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
